import {
  Button,
  DatePicker,
  IconButton,
  Input,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  StatusPill,
  Switch,
  TimePicker,
  formatMinutes,
  toMinutes,
  useIsWideViewport,
  useSnackbar,
  type CivilDate,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, PencilLine, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { choiceChips } from '../../app/choice-filters.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  MultiFilter,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { SchedulingView } from '../../app/list-views.js';
import { useCollectionView } from '../../app/use-collection-view.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  getApiError,
  schedulingApi,
  type HolidayMode,
  type SchedulingHoliday,
} from '../../lib/api.js';
import { holidayDateLabel, holidayModeLabel, modeOptions } from './holiday-labels.js';
import { useSchedulingUnits } from './scheduling-units.js';
import { DialogActions } from './shared.js';

const ORIGIN_LABEL: Record<SchedulingHoliday['origin'], string> = {
  NATIONAL: 'Feriado nacional',
  OPTIONAL: 'Ponto facultativo',
  CUSTOM: 'Cadastrado',
};
const holidayColumns: Array<{ sort: 'date' | 'name'; label: string }> = [
  { sort: 'date', label: 'Data' },
  { sort: 'name', label: 'Feriado' },
];
function decidedLabel(holiday: SchedulingHoliday): string {
  const scope = {
    DEFAULT: 'Padrão do sistema',
    COMPANY: 'Escolha da empresa',
    UNIT: 'Escolha da unidade',
  }[holiday.decidedBy];
  return holiday.onlyThisYear ? `${scope}, só neste ano` : scope;
}

/// Feriados do ano (ADR 0087): nacionais calculados, os cadastrados e como a empresa trabalha em
/// cada um. Quem vê a agenda vê os feriados; decidir exige configurar a agenda.
export function HolidaysPanel({
  canManage,
  today,
  view,
  onView,
}: {
  canManage: boolean;
  today: CivilDate;
  view: SchedulingView;
  onView: ViewSetter<SchedulingView>;
}) {
  const { units } = useSchedulingUnits();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();
  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';
  const year = view.holidayYear ?? Number(today.slice(0, 4));
  const unitId = units.length > 1 && view.holidayUnit ? view.holidayUnit : null;
  const [editing, setEditing] = useState<SchedulingHoliday>();
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<SchedulingHoliday>();
  const holidays = useQuery({
    queryKey: ['company', 'scheduling', 'holidays', year, unitId],
    queryFn: () => schedulingApi.listHolidays({ year, businessUnitId: unitId }),
    retry: false,
  });
  const search = view.holidaySearch.trim().toLocaleLowerCase('pt-BR');
  const rows = (holidays.data?.holidays ?? [])
    .filter(
      (holiday) =>
        (!search || holiday.name.toLocaleLowerCase('pt-BR').includes(search)) &&
        (view.holidayMode.length === 0 || view.holidayMode.includes(holiday.mode)),
    )
    .sort((left, right) => {
      const order =
        view.holidaySort === 'name'
          ? left.name.localeCompare(right.name, 'pt-BR')
          : left.date.localeCompare(right.date);
      return view.holidayDirection === 'asc' ? order : -order;
    });
  function toggleSort(column: 'date' | 'name') {
    if (view.holidaySort === column)
      onView({ holidayDirection: view.holidayDirection === 'asc' ? 'desc' : 'asc' });
    else onView({ holidaySort: column, holidayDirection: 'asc' });
  }
  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
  }
  async function remove(holiday: SchedulingHoliday) {
    try {
      await schedulingApi.removeHoliday(holiday.key.slice('custom:'.length));
      await refresh();
      setRemoving(undefined);
      snackbar.success('Feriado removido.');
    } catch (cause) {
      snackbar.failure(getApiError(cause));
    }
  }
  /// Feriado cadastrado só se remove no alcance em que foi cadastrado.
  const removable = (holiday: SchedulingHoliday) =>
    canManage && holiday.origin === 'CUSTOM' && holiday.businessUnitId === unitId;
  const count = `${String(rows.length)} ${rows.length === 1 ? 'feriado' : 'feriados'}`;
  const actions = (holiday: SchedulingHoliday) => (
    <div className="flex justify-end gap-1">
      <IconButton
        label={`${canManage ? 'Definir' : 'Ver'} ${holiday.name}`}
        icon={<PencilLine className="size-4" />}
        onClick={() => setEditing(holiday)}
      />
      {removable(holiday) ? (
        <IconButton
          label={`Remover ${holiday.name}`}
          tone="danger"
          icon={<Trash2 className="size-4" />}
          onClick={() => setRemoving(holiday)}
        />
      ) : null}
    </div>
  );
  const years = [year - 1, year, year + 1, year + 2]
    .filter((one) => one >= 2000 && one <= 2100)
    .map((one) => ({ value: String(one), label: String(one) }));
  return (
    <div role="tabpanel">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <Select
            label="Ano"
            value={String(year)}
            options={years}
            onValueChange={(next) => onView({ holidayYear: Number(next) })}
          />
          {units.length > 1 ? (
            <Select
              label="Vale para"
              value={unitId ?? ''}
              options={[
                { value: '', label: 'Empresa (todas as unidades)' },
                ...units.map((unit) => ({ value: unit.id, label: unit.displayName })),
              ]}
              onValueChange={(holidayUnit) => onView({ holidayUnit })}
            />
          ) : null}
        </div>
        {canManage ? (
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" /> Cadastrar feriado
          </Button>
        ) : null}
      </div>
      <FilterBar
        searchId="holiday-search"
        searchLabel="Buscar feriado"
        searchPlaceholder="Nome do feriado"
        search={view.holidaySearch}
        onSearchChange={(holidaySearch) => onView({ holidaySearch }, { replace: true })}
        chips={choiceChips('holidayMode', view.holidayMode, modeOptions, (holidayMode) =>
          onView({ holidayMode }),
        )}
        onClear={() => onView({ holidayMode: [] })}
        count={count}
        resultsLabel={`Ver ${count}`}
      >
        <MultiFilter
          label="Situação"
          itemNoun="situações"
          allLabel="Todas as situações"
          options={modeOptions}
          value={view.holidayMode}
          onChange={(holidayMode) => onView({ holidayMode })}
        />
      </FilterBar>
      {holidays.isLoading ? (
        <LoadingCard label="Carregando feriados" />
      ) : holidays.error ? (
        <ErrorCard message={getApiError(holidays.error)} />
      ) : rows.length === 0 ? (
        <EmptyCard
          icon={CalendarOff}
          title="Nenhum feriado encontrado"
          description="Ajuste a busca ou a situação para ver os feriados do ano."
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? (
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Feriados de {year}</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {holidayColumns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.holidaySort}
                      direction={view.holidayDirection}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th className="px-5 py-3 text-xs">Situação</th>
                  <th className="w-24">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {rows.map((holiday) => (
                  <tr key={holiday.key}>
                    <td className="px-5 py-4 text-xs font-semibold">
                      {holidayDateLabel(holiday.date)}
                    </td>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">{holiday.name}</strong>
                      <span className="text-xs text-content-muted">
                        {ORIGIN_LABEL[holiday.origin]}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs">
                      <ModePill holiday={holiday} />
                      <span className="mt-1 block text-content-faint">{decidedLabel(holiday)}</span>
                    </td>
                    <td className="px-3 py-4">{actions(holiday)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div>
              <SortBar
                label="Ordenação dos feriados"
                columns={holidayColumns}
                sort={view.holidaySort}
                direction={view.holidayDirection}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {rows.map((holiday) => (
                  <li key={holiday.key} className="flex items-start gap-3 p-4">
                    <CalendarOff className="mt-1 size-5 shrink-0 text-brand-400" />
                    <div className="min-w-0 flex-1">
                      <strong className="block truncate text-sm">{holiday.name}</strong>
                      <span className="block text-xs text-content-muted">
                        {holidayDateLabel(holiday.date)} · {ORIGIN_LABEL[holiday.origin]}
                      </span>
                      <div className="mt-1">
                        <ModePill holiday={holiday} />
                      </div>
                    </div>
                    {actions(holiday)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {editing ? (
        <HolidayChoiceDialog
          holiday={editing}
          unitId={unitId}
          canManage={canManage}
          multipleUnits={units.length > 1}
          onClose={() => setEditing(undefined)}
          onSaved={refresh}
        />
      ) : null}
      {creating ? (
        <HolidayCreateDialog
          today={today}
          unitId={unitId}
          onClose={() => setCreating(false)}
          onSaved={refresh}
        />
      ) : null}
      {removing ? (
        <Modal
          open
          onOpenChange={() => setRemoving(undefined)}
          title="Remover feriado"
          description="A agenda e o portal voltam a seguir o expediente nesse dia."
        >
          <p className="text-sm">
            Remover <strong>{removing.name}</strong>?
          </p>
          <DialogActions
            label="Remover feriado"
            onCancel={() => setRemoving(undefined)}
            onSubmit={() => void remove(removing)}
          />
        </Modal>
      ) : null}
    </div>
  );
}

function ModePill({ holiday }: { holiday: SchedulingHoliday }) {
  const tone =
    holiday.mode === 'CLOSED' ? 'danger' : holiday.mode === 'OPEN' ? 'positive' : 'warning';
  return <StatusPill tone={tone}>{holidayModeLabel(holiday)}</StatusPill>;
}

function HolidayChoiceDialog({
  holiday,
  unitId,
  canManage,
  multipleUnits,
  onClose,
  onSaved,
}: {
  holiday: SchedulingHoliday;
  unitId: string | null;
  canManage: boolean;
  multipleUnits: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const snackbar = useSnackbar();
  const year = Number(holiday.date.slice(0, 4));
  // Nacional pode ter escolha só para um ano; o cadastrado já é da empresa e vale sempre.
  const yearly = holiday.origin !== 'CUSTOM';
  const [mode, setMode] = useState<HolidayMode>(holiday.mode);
  const [start, setStart] = useState<string | null>(
    holiday.startMinute === null ? '09:00' : formatMinutes(holiday.startMinute),
  );
  const [end, setEnd] = useState<string | null>(
    holiday.endMinute === null ? '13:00' : formatMinutes(holiday.endMinute),
  );
  const [onlyThisYear, setOnlyThisYear] = useState(holiday.onlyThisYear);
  // Equipe reduzida marca pessoas e salas, que são de uma unidade: na visão da empresa com várias
  // unidades, a escolha é feita em cada unidade.
  const reducedAllowed = !(multipleUnits && unitId === null);
  const resources = useQuery({
    queryKey: ['company', 'scheduling', 'holiday-resources', unitId],
    queryFn: () =>
      schedulingApi.listResources({
        ...(unitId ? { businessUnitId: unitId } : {}),
        activity: 'ACTIVE',
        kind: 'ALL',
        sort: 'displayName',
        direction: 'asc',
      }),
    enabled: reducedAllowed,
    retry: false,
  });
  const options = (resources.data ?? []).map((resource) => ({
    value: resource.id,
    label: resource.kind === 'ROOM' ? `${resource.displayName} (sala)` : resource.displayName,
  }));
  const [picked, setPicked] = useState<string[] | null>(
    holiday.mode === 'REDUCED' ? holiday.resourceIds : null,
  );
  // Sem escolha anterior, a lista começa com todos marcados: desmarca-se quem folga.
  const working = picked ?? options.map((option) => option.value);
  const [error, setError] = useState<string>();
  const hoursError =
    mode === 'REDUCED' && (!start || !end || toMinutes(end) <= toMinutes(start))
      ? 'O fim deve ser posterior ao início.'
      : undefined;
  const ownScope = holiday.decidedBy === (unitId ? 'UNIT' : 'COMPANY');
  async function save(next: HolidayMode | null) {
    if (next === 'REDUCED' && (hoursError || working.length === 0)) {
      setError(hoursError ?? 'Marque quem trabalha no feriado.');
      return;
    }
    try {
      await schedulingApi.saveHolidayChoice({
        businessUnitId: unitId,
        holidayKey: holiday.key,
        year: (next === null ? holiday.onlyThisYear : yearly && onlyThisYear) ? year : null,
        mode: next,
        startMinute: next === 'REDUCED' ? toMinutes(start!) : null,
        endMinute: next === 'REDUCED' ? toMinutes(end!) : null,
        resourceIds: next === 'REDUCED' ? working : [],
      });
      await onSaved();
      snackbar.success(next === null ? 'O feriado voltou a seguir o padrão.' : 'Feriado salvo.');
      onClose();
    } catch (cause) {
      setError(getApiError(cause));
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title={holiday.name}
      description={`${holidayDateLabel(holiday.date)} · ${decidedLabel(holiday)}`}
    >
      <div className="grid gap-4">
        <SegmentedControl
          label="Como a empresa trabalha neste dia"
          options={modeOptions}
          value={mode}
          onChange={(next) => {
            if (!canManage) return;
            setError(undefined);
            setMode(next);
          }}
        />
        <p className="text-xs text-content-muted">
          {mode === 'CLOSED'
            ? 'O portal não oferece horário. Na agenda, marcar neste dia mostra um aviso, sem impedir.'
            : mode === 'OPEN'
              ? 'O dia segue o expediente de sempre, como se não fosse feriado.'
              : 'O portal só oferece horário de quem está marcado, dentro do horário do dia. Na agenda, marcar com quem folga ou fora desse horário mostra um aviso, sem impedir.'}
        </p>
        {mode === 'REDUCED' ? (
          reducedAllowed ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <TimePicker
                  label="Início"
                  value={start}
                  onValueChange={setStart}
                  disabled={!canManage}
                />
                <TimePicker
                  label="Fim"
                  value={end}
                  onValueChange={setEnd}
                  error={hoursError}
                  disabled={!canManage}
                />
              </div>
              <MultiSelect
                label="Quem trabalha"
                itemNoun="profissionais e salas"
                allLabel="Toda a equipe e todas as salas"
                noneLabel="Ninguém"
                searchPlaceholder="Buscar profissional ou sala"
                options={options}
                selected={working}
                loading={resources.isLoading}
                onChange={(next) => {
                  if (canManage) setPicked(next);
                }}
              />
            </>
          ) : (
            <p role="status" className="text-xs font-medium text-warning">
              Equipe reduzida marca profissionais e salas de uma unidade. Em Vale para, escolha a
              unidade e defina o feriado nela.
            </p>
          )
        ) : null}
        {yearly && canManage ? (
          <Switch checked={onlyThisYear} onChange={setOnlyThisYear}>
            Só em {year}; nos outros anos segue a escolha de sempre
          </Switch>
        ) : null}
        {error ? (
          <p role="alert" className="text-xs font-medium text-danger">
            {error}
          </p>
        ) : null}
      </div>
      {canManage ? (
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-surface-border pt-5">
          {ownScope ? (
            <Button type="button" variant="ghost" onClick={() => void save(null)}>
              {unitId ? 'Seguir a escolha da empresa' : 'Voltar ao padrão do sistema'}
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <Button
            type="button"
            disabled={mode === 'REDUCED' && !reducedAllowed}
            onClick={() => void save(mode)}
          >
            Salvar feriado
          </Button>
        </div>
      ) : (
        <div className="mt-6 flex justify-end border-t border-surface-border pt-5">
          <Button type="button" variant="outline" onClick={onClose}>
            Voltar
          </Button>
        </div>
      )}
    </Modal>
  );
}

function HolidayCreateDialog({
  today,
  unitId,
  onClose,
  onSaved,
}: {
  today: CivilDate;
  unitId: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const snackbar = useSnackbar();
  const { units } = useSchedulingUnits();
  const [name, setName] = useState('');
  const [date, setDate] = useState<string | null>(null);
  const [repeatsYearly, setRepeatsYearly] = useState(false);
  const [scope, setScope] = useState(unitId ?? '');
  const [errors, setErrors] = useState<{ name?: string; date?: string; request?: string }>({});
  async function save() {
    const found = {
      ...(name.trim().length < 2 ? { name: 'Informe o nome do feriado.' } : {}),
      ...(date ? {} : { date: 'Escolha a data.' }),
    };
    setErrors(found);
    if (Object.keys(found).length) return;
    try {
      await schedulingApi.createHoliday({
        businessUnitId: scope || null,
        name: name.trim(),
        date: date!,
        repeatsYearly,
      });
      await onSaved();
      snackbar.success('Feriado cadastrado.');
      onClose();
    } catch (cause) {
      setErrors({ request: getApiError(cause) });
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Cadastrar feriado"
      description="Feriado estadual, municipal ou data própria, como um recesso. Ele começa fechado; defina depois se a empresa trabalha."
    >
      <div className="grid gap-4">
        <Input
          label="Nome do feriado"
          value={name}
          maxLength={80}
          error={errors.name}
          onChange={(event) => setName(event.target.value)}
        />
        <DatePicker
          label="Data"
          value={date}
          onValueChange={setDate}
          today={today}
          error={errors.date}
        />
        <Switch checked={repeatsYearly} onChange={setRepeatsYearly}>
          Repete todo ano na mesma data
        </Switch>
        {units.length > 1 ? (
          <Select
            label="Vale para"
            value={scope}
            options={[
              { value: '', label: 'Empresa (todas as unidades)' },
              ...units.map((unit) => ({ value: unit.id, label: unit.displayName })),
            ]}
            onValueChange={setScope}
          />
        ) : null}
        {errors.request ? (
          <p role="alert" className="text-xs font-medium text-danger">
            {errors.request}
          </p>
        ) : null}
      </div>
      <DialogActions label="Cadastrar feriado" onCancel={onClose} onSubmit={() => void save()} />
    </Modal>
  );
}
